import React, { useEffect, useRef } from 'react';
import { Camera, Upload, RefreshCw, AlertCircle, FileText, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CameraErrorType } from '@/hooks/useCameraStream';

export function CameraScanner({
  camera,
  onCapture,
  onDeviceUpload,
  onManualEntry
}) {
  const fileInputRef = useRef(null);
  const {
    videoRef,
    isReady,
    error,
    errorType,
    facingMode,
    startStream,
    switchFacingMode,
    captureFrame
  } = camera;

  // Auto-start camera when component mounts
  useEffect(() => {
    startStream();
  }, [startStream]);

  const handleCaptureClick = async () => {
    try {
      const result = await captureFrame();
      if (result) {
        onCapture(result);
      }
    } catch (err) {
      console.error('Frame capture failed:', err);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onDeviceUpload(file);
    }
  };

  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-stone-950 border border-stone-800 shadow-2xl flex flex-col items-center">
      {/* Top Bar with Status and Actions */}
      <div className="w-full z-20 px-4 py-3 bg-stone-900/80 backdrop-blur-md flex items-center justify-between border-b border-stone-800/80 text-stone-200">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isReady ? 'bg-emerald-400' : 'bg-amber-400'} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isReady ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
          </span>
          <span className="text-xs font-medium tracking-wide uppercase text-stone-400">
            {isReady ? 'Prescription Scanner Active' : 'Initializing Camera...'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {isReady && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={switchFacingMode}
              className="text-stone-300 hover:text-white hover:bg-stone-800 h-8 px-2.5 text-xs flex items-center gap-1.5"
              title="Flip Camera"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Flip</span>
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onManualEntry}
            className="text-amber-400 hover:text-amber-300 hover:bg-stone-800 h-8 px-2.5 text-xs flex items-center gap-1.5"
          >
            <FileText className="h-3.5 w-3.5" />
            <span>Type Manually</span>
          </Button>
        </div>
      </div>

      {/* Main Viewfinder / Camera Surface */}
      <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] max-h-[68vh] bg-black flex items-center justify-center overflow-hidden">
        {/* HTML5 Video element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover transition-opacity duration-500 ${isReady ? 'opacity-100' : 'opacity-0'}`}
        />

        {/* Viewfinder Overlay with Document Frame */}
        {isReady && (
          <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center p-6">
            {/* Guide Frame */}
            <div className="relative w-full max-w-md h-full max-h-[82%] border-2 border-emerald-500/60 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.50)] transition-all">
              {/* Corner brackets */}
              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-lg" />
              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-lg" />
              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-lg" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-lg" />

              {/* Animated scan line */}
              <div className="absolute left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_12px_rgba(52,211,153,0.8)] animate-pulse top-1/3" />

              {/* Patient Name Targeting Hint */}
              <div className="absolute top-4 left-4 right-4 bg-stone-900/80 backdrop-blur-sm border border-stone-700/60 rounded-lg p-2 text-center text-xs text-stone-300">
                Align prescription header with <strong className="text-emerald-400">Patient Name</strong> visible inside frame
              </div>
            </div>
          </div>
        )}

        {/* Camera Permission / Error Fallback State */}
        {error && (
          <div className="absolute inset-0 bg-stone-950/95 flex flex-col items-center justify-center p-6 text-center z-10">
            <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-3 text-amber-400">
              <AlertCircle className="h-7 w-7" />
            </div>
            <h3 className="text-base font-semibold text-stone-200 mb-1">
              {errorType === CameraErrorType.NOT_ALLOWED
                ? 'Camera Permission Needed'
                : 'Camera Unavailable'}
            </h3>
            <p className="text-sm text-stone-400 max-w-sm mb-5 leading-relaxed">
              {error}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 h-11"
              >
                <Upload className="h-4 w-4" />
                Upload from Device
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={onManualEntry}
                className="w-full border-stone-700 text-stone-300 hover:bg-stone-800 h-11"
              >
                Enter Manually
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden File Input for Device Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Bottom Controls Bar */}
      <div className="w-full bg-stone-900 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-stone-800">
        {/* Device Upload Button */}
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full sm:w-auto border-stone-700 bg-stone-800/80 text-stone-200 hover:bg-stone-700 hover:text-white flex items-center justify-center gap-2 h-11 px-5"
        >
          <Upload className="h-4 w-4 text-emerald-400" />
          <span>Upload from Device</span>
        </Button>

        {/* Primary Shutter / Capture Button */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!isReady}
            onClick={handleCaptureClick}
            className="group relative flex items-center justify-center w-16 h-16 rounded-full bg-stone-800 border-4 border-stone-600 hover:border-emerald-500 active:scale-95 transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
            aria-label="Capture Prescription Photo"
          >
            <div className="w-11 h-11 rounded-full bg-emerald-500 group-hover:bg-emerald-400 group-active:scale-90 transition-all flex items-center justify-center text-white">
              <Camera className="h-5 w-5" />
            </div>
          </button>
        </div>

        {/* Manual Fallback Link */}
        <button
          type="button"
          onClick={onManualEntry}
          className="text-xs text-stone-400 hover:text-stone-200 flex items-center gap-1 group py-1"
        >
          <span>Skip to Manual Prescription</span>
          <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
}
